import { Message, MessageOption, OptionsSource } from "./types";

export const CHAT_STARTER_IDS = [
  "recommendSession",
  "stressfulSituation",
  "sleepBetter",
  "scoresMeaning",
  "stressPatterns",
] as const;

export type ChatStarterId = (typeof CHAT_STARTER_IDS)[number];

export interface ChatStarter {
  id: ChatStarterId;
  labelKey: string;
  messageKey: string;
}

export const CHAT_STARTERS: ChatStarter[] = CHAT_STARTER_IDS.map((id) => ({
  id,
  labelKey: `ori:chat.starters.${id}.label`,
  messageKey: `ori:chat.starters.${id}.message`,
}));

/**
 * The pills are presented in random order so per-pill tap data isn't
 * position-biased. Shuffle once per display; the tap event records both
 * the starter id and its displayed position.
 */
export const shuffledStarters = (): ChatStarter[] => {
  const starters = [...CHAT_STARTERS];
  for (let i = starters.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [starters[i], starters[j]] = [starters[j], starters[i]];
  }
  return starters;
};

/** Technical user turns (`__START__`, JSON submissions) are never rendered. */
const isTechnicalMessage = (message: Pick<Message, "role" | "content">) => {
  if (message.role !== "user") return false;
  const content = message.content.trim();
  return content.startsWith("__") || content.startsWith("{");
};

interface ShouldShowChatStartersArgs {
  enabled: boolean;
  isLoadingHistory: boolean;
  isAwaitingReply: boolean;
  /** Server signal — pills are for first-time Ori users only. */
  hasEverMessaged: boolean;
  messages: Pick<Message, "role" | "content">[];
}

/**
 * The starter pills show only on a first-time user's fresh conversation:
 * they've never messaged Ori before, the greeting has landed, and they
 * haven't sent anything yet. Technical sends (`__START__`) never enter
 * the transcript, but guard anyway so a leaked one can't count as the
 * user speaking.
 */
export const shouldShowChatStarters = ({
  enabled,
  isLoadingHistory,
  isAwaitingReply,
  hasEverMessaged,
  messages,
}: ShouldShowChatStartersArgs): boolean => {
  if (!enabled || isLoadingHistory || isAwaitingReply || hasEverMessaged) {
    return false;
  }

  const hasGreeting = messages.some(
    (message) => message.role === "assistant" && message.content !== ""
  );
  const hasUserMessage = messages.some(
    (message) => message.role === "user" && !isTechnicalMessage(message)
  );

  return hasGreeting && !hasUserMessage;
};

interface SelectDynamicStartersArgs {
  /** Flag on, and nothing else holds the starters back (draft, view, QA). */
  enabled: boolean;
  isLoadingHistory: boolean;
  /** A turn is in flight, including Ori's reply still streaming. */
  isSending: boolean;
  /** Coach serves starter options to returning learners only. */
  hasEverMessaged: boolean;
  messages: Pick<
    Message,
    "role" | "content" | "message_id" | "options" | "options_source"
  >[];
  /** The message whose starters the learner typed past. */
  dismissedMessageId: string | null;
}

export interface DynamicStarters {
  messageId: string;
  options: MessageOption[];
  source: OptionsSource;
}

/**
 * The options on Ori's newest message, when Coach marked them as starters (a
 * source). Options without one are the model's own and never render.
 */
export const selectDynamicStarters = ({
  enabled,
  isLoadingHistory,
  isSending,
  hasEverMessaged,
  messages,
  dismissedMessageId,
}: SelectDynamicStartersArgs): DynamicStarters | null => {
  if (!enabled || isLoadingHistory || isSending || !hasEverMessaged) {
    return null;
  }

  let newest: SelectDynamicStartersArgs["messages"][number] | undefined;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (!isTechnicalMessage(messages[i])) {
      newest = messages[i];
      break;
    }
  }
  if (
    !newest ||
    newest.role !== "assistant" ||
    !newest.message_id ||
    !newest.options?.length ||
    !newest.options_source ||
    newest.message_id === dismissedMessageId
  ) {
    return null;
  }

  return {
    messageId: newest.message_id,
    options: newest.options,
    source: newest.options_source,
  };
};
